
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function PersonnalisationsPage() {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    typeCreation: '',
    description: '',
    budget: '',
    delai: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    // SEO JSON-LD pour la page personnalisations
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Créations personnalisées - Les Carnets de Lolotte",
      "description": "Service de créations artisanales personnalisées en papier plié. Livres pliés avec photos, prénoms, symboles sur mesure.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/personnalisations`,
      "provider": {
        "@type": "Person",
        "name": "Nathalie",
        "jobTitle": "Artisane créatrice"
      },
      "offers": {
        "@type": "Offer",
        "priceRange": "35-50 EUR",
        "priceCurrency": "EUR",
        "description": "Créations personnalisées selon complexité"
      },
      "serviceType": "Artisanat personnalisé",
      "category": "Art et artisanat",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Services de personnalisation",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "Livre plié avec photo personnalisée",
              "category": "Création personnalisée"
            }
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    // Mise à jour des meta tags
    document.title = "Créations personnalisées - Livres pliés sur mesure | Les Carnets de Lolotte";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Commandez votre création personnalisée en papier plié. Livres avec photos, prénoms, symboles sur mesure. Artisanat français fait main. Devis gratuit.');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('nom', formData.nom);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('typeCreation', formData.typeCreation);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('budget', formData.budget);
      formDataToSend.append('delai', formData.delai);

      // Ajouter le fichier photo si présent
      const fileInput = document.getElementById('photo') as HTMLInputElement;
      if (fileInput?.files?.[0]) {
        formDataToSend.append('photo', fileInput.files[0]);
      }

      const response = await fetch('https://readdy.ai/api/form/d3sj9gc1cr8rcdqfdr9g', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          nom: '',
          email: '',
          typeCreation: '',
          description: '',
          budget: '',
          delai: ''
        });
        // Reset file input
        if (fileInput) fileInput.value = '';
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReturnToBoutique = () => {
    window.location.href = '/boutique';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <h1 className="text-4xl font-serif text-rose-800 text-center">
            Créations personnalisées – émotions sur mesure 💕
          </h1>
          <p className="text-rose-600 text-center mt-2 italic">
            Parce qu'un livre peut raconter bien plus qu'une histoire…
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Introduction */}
        <section className="text-center mb-12">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-rose-100">
            <p className="text-rose-700 text-lg leading-relaxed mb-6">
              Offrez (ou offrez-vous) une création entièrement personnalisée :
              un livre plié unique, orné d'une photo, d'un prénom, d'un mot doux ou d'un symbole particulier.
            </p>
            <p className="text-rose-600 mb-6">
              Chaque pièce est conçue à la main, sur commande, avec le plus grand soin.
              C'est un cadeau poétique, intime et durable – idéal pour un anniversaire, un mariage, Noël ou simplement pour dire "je t'aime". 💫
            </p>
            <div className="text-2xl font-semibold text-rose-800 mb-2">
              À partir de 35 €
            </div>
            <p className="text-rose-600 text-sm">
              selon la complexité du modèle et la personnalisation choisie
            </p>
          </div>
        </section>

        {/* Galerie d'exemples */}
        <section className="mb-16">
          <h2 className="text-3xl font-serif text-rose-800 text-center mb-8">
            Exemples de créations personnalisées
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Livre plié personnalisé - Photo en relief */}
            <article className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <img 
                src="https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/46c9733f464f1d0031a3113d5ad95597.png" 
                alt="Livre plié personnalisé avec photo de chien en relief"
                className="w-full h-64 object-contain bg-gray-50"
              />
              <div className="p-6">
                <h3 
                  className="text-xl font-bold text-[#C49B86] mb-3"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Livre plié personnalisé – Photo en relief
                </h3>
                <p 
                  className="text-[#6B5E5A] text-sm leading-relaxed mb-4"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  Offrez un souvenir unique et poétique : un véritable livre plié à la main, où la photo de votre choix prend vie dans les pages. 
                  Chaque création est entièrement personnalisée à partir de votre image, soigneusement imprimée et intégrée page après page, pour un rendu en volume à la fois artistique et émouvant.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-[#6B5E5A]">
                    <i className="ri-check-line text-[#C49B86] mr-2"></i>
                    <span style={{ fontFamily: 'Poppins, sans-serif' }}>Réalisé à la main à partir de livres recyclés</span>
                  </div>
                  <div className="flex items-center text-sm text-[#6B5E5A]">
                    <i className="ri-check-line text-[#C49B86] mr-2"></i>
                    <span style={{ fontFamily: 'Poppins, sans-serif' }}>Photo imprimée avec précision pour un effet 3D</span>
                  </div>
                  <div className="flex items-center text-sm text-[#6B5E5A]">
                    <i className="ri-check-line text-[#C49B86] mr-2"></i>
                    <span style={{ fontFamily: 'Poppins, sans-serif' }}>Dimensions : environ 20 cm de haut</span>
                  </div>
                  <div className="flex items-center text-sm text-[#6B5E5A]">
                    <i className="ri-clock-line text-[#C49B86] mr-2"></i>
                    <span style={{ fontFamily: 'Poppins, sans-serif' }}>Délai : 5 à 7 jours selon la complexité</span>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <span 
                    className="text-2xl font-bold text-[#C49B86]"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    40 €
                  </span>
                </div>
                <div className="flex justify-center">
                  <Link
                    to="/contact"
                    className="bg-[#F8EDEA] text-[#C49B86] px-6 py-3 rounded-full font-semibold border-2 border-[#C49B86] hover:bg-[#C49B86] hover:text-white transition-colors whitespace-nowrap"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    Commander ma photo 💕
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </section>

        {/* Encadré informatif sur les délais */}
        <section className="mb-8">
          <div className="bg-[#F8EDEA] border-2 border-[#C49B86] rounded-2xl p-8 shadow-sm max-w-4xl mx-auto">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 text-3xl">
                🕊️
              </div>
              <div className="flex-1 text-center">
                <p className="text-[#6B5E5A] text-lg leading-relaxed italic" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Chaque création étant entièrement réalisée à la main, le temps de fabrication peut varier selon le nombre de pages à plier et le niveau de détail souhaité.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Formulaire de contact */}
        <section id="formulaire-contact" className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-rose-100">
          <h2 className="text-3xl font-serif text-rose-800 text-center mb-8">
            Demande de création personnalisée
          </h2>

          {isSubmitted ? (
            <div className="text-center py-16">
              <div className="bg-[#F8EDEA] border-2 border-[#C49B86] rounded-2xl p-12 shadow-lg max-w-3xl mx-auto">
                <div className="text-6xl mb-6">🕊️</div>
                <h3 className="text-3xl font-bold text-[#C49B86] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Merci pour votre demande 💕
                </h3>
                
                <div className="space-y-6 text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <p className="text-lg leading-relaxed">
                    Votre message a bien été envoyé et sera lu avec attention.
                  </p>
                  
                  <p className="text-lg leading-relaxed">
                    Chaque création personnalisée est une aventure unique, et je prends toujours le temps d'en comprendre l'intention et les détails.
                  </p>
                  
                  <p className="text-lg leading-relaxed italic text-[#C49B86]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    🌸 Je vous répondrai sous 24 à 48h pour confirmer la faisabilité, le tarif et les délais de fabrication.
                  </p>
                  
                  <p className="text-lg leading-relaxed">
                    Merci pour votre confiance, et à très bientôt pour donner vie à votre histoire en papier ✨
                  </p>
                </div>
                
                <div className="mt-8 space-y-4">
                  <button
                    onClick={handleReturnToBoutique}
                    className="bg-[#C49B86] text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-[#B97A72] transition-colors whitespace-nowrap cursor-pointer shadow-lg"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    Revenir à la boutique
                  </button>
                  
                  <div>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="text-[#C49B86] hover:text-[#B97A72] transition-colors underline whitespace-nowrap cursor-pointer"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      Faire une nouvelle demande
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              {/* Nom */}
              <div>
                <label htmlFor="nom" className="block text-rose-700 font-medium mb-2">
                  Votre prénom et nom *
                </label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-rose-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 bg-rose-50/50 text-rose-800"
                  placeholder="Votre prénom et nom"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-rose-700 font-medium mb-2">
                  Adresse e-mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-rose-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 bg-rose-50/50 text-rose-800"
                  placeholder="votre@email.com"
                />
              </div>

              {/* Type de création */}
              <div>
                <label htmlFor="typeCreation" className="block text-rose-700 font-medium mb-2">
                  Type de création souhaitée *
                </label>
                <select
                  id="typeCreation"
                  name="typeCreation"
                  value={formData.typeCreation}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-rose-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 bg-rose-50/50 text-rose-800 pr-8"
                >
                  <option value="">Choisissez un type de création</option>
                  <option value="livre-photo">Livre plié avec photo</option>
                  <option value="livre-prenom">Livre plié avec prénom / mot</option>
                  <option value="livre-symbole">Livre plié symbole (cœur, infini, ange, etc.)</option>
                  <option value="autre">Autre (à préciser ci-dessous)</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-rose-700 font-medium mb-2">
                  Description de votre projet *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-rose-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 bg-rose-50/50 text-rose-800 resize-none"
                  placeholder="Exemple : Je voudrais un livre plié avec la photo de mon couple et nos prénoms, dans des tons dorés."
                />
                <p className="text-rose-500 text-sm mt-1">
                  {formData.description.length}/500 caractères
                </p>
              </div>

              {/* Photo */}
              <div>
                <label htmlFor="photo" className="block text-rose-700 font-medium mb-2">
                  Photo ou image à joindre (facultatif)
                </label>
                <input
                  type="file"
                  id="photo"
                  name="photo"
                  accept="image/jpeg,image/png,image/jpg"
                  className="w-full px-4 py-3 border border-rose-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 bg-rose-50/50 text-rose-800"
                />
                <p className="text-rose-500 text-sm mt-1">
                  Formats acceptés : JPG, PNG (Non récupérable)
                </p>
              </div>

              {/* Budget */}
              <div>
                <label htmlFor="budget" className="block text-rose-700 font-medium mb-2">
                  Budget approximatif (facultatif)
                </label>
                <input
                  type="text"
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-rose-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 bg-rose-50/50 text-rose-800"
                  placeholder="Ex: 50€, entre 40 et 60€..."
                />
              </div>

              {/* Délai */}
              <div>
                <label htmlFor="delai" className="block text-rose-700 font-medium mb-2">
                  Délai souhaité (facultatif)
                </label>
                <select
                  id="delai"
                  name="delai"
                  value={formData.delai}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-rose-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 bg-rose-50/50 text-rose-800 pr-8"
                >
                  <option value="">Choisissez un délai</option>
                  <option value="peu-importe">Peu importe</option>
                  <option value="standard">Délai standard 7 jours</option>
                  <option value="express">Délai express 3 jours</option>
                </select>
              </div>

              {/* Bouton de soumission */}
              <div className="text-center pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-rose-400 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-rose-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande 💕'}
                </button>
              </div>

              <div className="text-center text-rose-600 text-sm mt-4">
                <p>Délai de fabrication estimé : 5 à 7 jours ouvrés</p>
                <p className="italic mt-2">
                  Chaque demande est étudiée avec soin, et je reviens vers vous sous 24h à 48h pour confirmer la faisabilité et le devis personnalisé.
                </p>
              </div>
            </form>
          )}
        </section>

        {/* Encadré Devis & Paiement personnalisé */}
        <section className="mt-8">
          <div className="bg-[#F8EDEA] border-2 border-[#C49B86] rounded-2xl p-8 shadow-sm max-w-4xl mx-auto">
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex-shrink-0 text-3xl">
                💳
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-semibold text-[#C49B86] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Devis & Paiement personnalisé
                </h3>
              </div>
            </div>
            
            <div className="text-center space-y-4">
              <p className="text-[#6B5E5A] text-lg leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Après réception de votre demande, je vous contacte sous 24 à 48h pour préciser les détails de votre création (photo, motif, couleur, etc.).
              </p>
              
              <p className="text-[#6B5E5A] text-lg leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Une fois le projet validé ensemble, vous recevrez un <strong>lien de paiement sécurisé Stripe</strong> correspondant à votre devis personnalisé.
              </p>
              
              <p className="text-[#C49B86] text-lg leading-relaxed italic mt-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                🌸 Le règlement confirme la mise en fabrication de votre création.
              </p>
              
              <p className="text-[#C49B86] text-lg leading-relaxed italic" style={{ fontFamily: 'Playfair Display, serif' }}>
                🕊️ Chaque commande sur mesure est unique, et le paiement sécurisé garantit la réservation de votre place dans le planning de création.
              </p>
            </div>
          </div>
        </section>

        {/* Phrase de clôture */}
        <div className="text-center mt-12">
          <p className="text-2xl font-serif text-rose-700 italic">
            Ici, chaque création devient une histoire à part entière. 💕
          </p>
        </div>
      </main>
    </div>
  );
}
